import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LuxonModule } from '@modules/luxon';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconButton } from '@angular/material/button';
import { Store } from '@ngrx/store';
import { selectGlobalFiltersState } from '@app/store/reducers';
import { MatCardModule } from '@angular/material/card';
import { LuxonDateRangePipe } from '@modules/luxon/pipes/luxon-date-range.pipe';
import {
  ConnectionMonitorService,
  ConnectionQuality,
} from '@modules/connection-monitor/services/connection-monitor.service';
import { Dictionary } from '@ngrx/entity';
import { stateViewerFeature } from '@modules/state-viewer/state-viewer.reducer';
import { AsyncPipe } from '@angular/common';
import { IfHasPrivilegesDirective } from '@modules/auth-utils/directives/if-has-privileges.directive';
import { RouterLink } from '@angular/router';
import type { StateViewerDialogComponent } from '@modules/state-viewer/state-viewer-dialog.component';
import { GlobalDateFilterDialogService } from '@app/core/components/global-date-filter-dialog/global-date-filter-dialog.service';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';

interface Status {
  color: string;
  iconName: string;
}

@UntilDestroy()
@Component({
    selector: 'app-status-bar',
    templateUrl: './status-bar.component.html',
    styleUrls: ['./status-bar.component.scss'],
    imports: [LuxonModule, MatIconModule, MatDialogModule, MatCardModule, LuxonDateRangePipe, AsyncPipe, MatIconButton, IfHasPrivilegesDirective, RouterLink]
})
export class StatusBarComponent implements OnInit {
  protected connectionMonitorService = inject(ConnectionMonitorService);

  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly globalDateFilterDialogService = inject(GlobalDateFilterDialogService);
  protected readonly stateViewerPreviouslyOpened$ = this.store.select(stateViewerFeature.selectOpened);
  protected readonly selectedTenant = this.store.selectSignal(userTenantFeature.selectSelectedTenant);
  protected readonly userHasAccessToMoreThanOneTenant =
    this.store.selectSignal(userTenantFeature.selectUserHasAccessToMoreThanOneTenant);

  statusMap: Dictionary<Status> = {
    'No Connection': { color: '#f44336', iconName: 'signal_wifi_bad' },
    'Gathering Data': { color: '#3f51b5', iconName: 'network_check'},
    Excellent: { color: '#3f51b5', iconName: 'wifi'},
    Good: { color: '#3f51b5', iconName: 'wifi'},
    Mediocre: { color: '#3f51b5', iconName: 'wifi_2_bar'},
    Bad: { color: '#3f51b5', iconName: 'wifi_1_bar'},
    Terrible: { color: '#3f51b5', iconName: 'wifi_1_bar'},
  };
  protected readonly connectionQuality = toSignal(this.connectionMonitorService.connectionQuality$, {
    initialValue: ConnectionQuality.GATHERING_DATA,
  });
  protected readonly shouldFadeIn = signal(false);
  private readonly globalFilters = this.store.selectSignal(selectGlobalFiltersState);
  protected readonly dateRange = computed(() => ({
    start: this.globalFilters()?.startAtOrAfter ?? null,
    end: this.globalFilters()?.endAtOrBefore ?? null,
  }));

  ngOnInit() {
    this.connectionMonitorService.connectionQuality$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.shouldFadeIn.set(true);
        setTimeout(() => this.shouldFadeIn.set(false), 500);
      });
  }

  onDateRangeClick() {
    void this.globalDateFilterDialogService.open();
  }

  protected async showStateViewerDialog() {
    const { StateViewerDialogComponent } = await import('@modules/state-viewer/state-viewer-dialog.component');
    this.dialog.open<StateViewerDialogComponent>(StateViewerDialogComponent, {
      minWidth: '85vw',
      maxHeight: '100vh',
    });
  }
}
